import {ThemeProvider} from "styled-components";
import {useReportedRouteError} from "errors/useReportedRouteError";
import {catalogFor} from "i18n/catalogs";
import {resolveActiveLocale, type Locale} from "i18n/localePreference";
import {LogoMark} from "nav/BrandMark";
import {isPlainPrimaryClick} from "nav/resourceNavigation";
import {resolveInitialMode} from "theme/resolveInitialMode";
import {darkTheme, lightTheme} from "theme/theme";
import {RouteErrorView} from "./ContentRouteError";
import {ErrorBackdrop, ErrorBrand} from "./RootRouteError.styles";

const THEMES = {light: lightTheme, dark: darkTheme};

/**
 * Lives outside RootLayout. Read the catalog and existing document theme stamp without re-entering
 * any app provider: one of those providers may be the component that threw.
 *
 * The caught error is REPORTED as well as logged (kitecorp/transferify-ui#2581): React Router still writes
 * it to the browser console, which is a record nobody on the team is sitting in front of, so
 * `useReportedRouteError` posts a bounded, anonymous record of it to the api's client-error ingest. No
 * exception data enters this VIEW — the reader is shown the same generic copy as before, and what the
 * report may contain is decided entirely in `errors/clientErrorReport.ts`.
 */
export function RootRouteError({forcedLocale}: {forcedLocale?: Locale}) {
	useReportedRouteError("ROOT_BOUNDARY", forcedLocale);
	const locale = resolveActiveLocale(forcedLocale);
	// The shared registry, never a ternary: a two-branch lookup answered English for every market past
	// the second, and this view is the one place a reader lands when the app itself has failed.
	const messages = catalogFor(locale);
	const stampedMode = typeof document === "undefined" ? null : document.documentElement.dataset.theme ?? null;
	const theme = THEMES[resolveInitialMode(stampedMode, false)];
	const retryHref = typeof window === "undefined" ? "/" : window.location.href;
	return (
		<ThemeProvider theme={theme}>
			<ErrorBackdrop>
				<RouteErrorView
					heading={messages.content.errorHeading}
					body={messages.errors.generic}
					retry={messages.content.errorRetry}
					retryHref={retryHref}
					onRetry={event => {
						if (!isPlainPrimaryClick(event)) return;
						// Preserve unconsumed URL tokens and force a new document even with a fragment.
						event.preventDefault();
						window.location.reload();
					}}
					lang={locale}
				>
					<ErrorBrand><LogoMark aria-hidden="true"/>Transferify</ErrorBrand>
				</RouteErrorView>
			</ErrorBackdrop>
		</ThemeProvider>
	);
}
